import React, { FC, useContext } from 'react'
import styled from 'styled-components'
import useSWR from 'swr'

import { Button } from '@Components/Button'
import { Image } from '@Components/Cloudinary/Image'
import { Modal, ModalProps } from '@Components/Modal'
import { MetadataContext } from '@Globals/Metadata'
import { UserContext } from '@Globals/User'

const Title = styled.span`
  display: block;
  text-align: center;
  padding-bottom: 30px;
  font-size: 1.25rem;
  text-transform: uppercase;
`

const ButtonsWrapper = styled.div`
  font-size: 1.125rem;

  .buttonContent {
    display: flex;

    .buttonImage {
      height: 32px;
      width: 32px;
    }

    .buttonText {
      margin: auto 0 auto 15px;
    }
  }
`

interface CountryDetectionData {
  curr: string
  dist: string
  hl: string
  redirect: string
}

interface CountryDetectionModalProps extends ModalProps {
  geoIPCountryCode: string
}

export const CountryDetectionModal: FC<CountryDetectionModalProps> = ({
  geoIPCountryCode,
  onHide,
  ...props
}: CountryDetectionModalProps) => {
  const { userData } = useContext(UserContext)
  const { page, store, storeURL } = useContext(MetadataContext)
  const country = store?.config?.countryCode || ''
  const yoloCountry = (userData?.otherCountry?.country || '')

  const params = new URLSearchParams()
  params.set('country', yoloCountry ? 'US' : geoIPCountryCode)
  params.set('pagetype', page?.type || '')
  params.set('pagetypeid', page?.id || '')
  const countryDetectionURL = storeURL('personality/index/geo', params)

  const { data } = useSWR(
    countryDetectionURL,
    async (input: RequestInfo, init?: RequestInit): Promise<CountryDetectionData | null> => {
      const res = await fetch(input, init)
      const json = await res.json()
      if (json.status !== 'OK') {
        return null
      }
      return JSON.parse(json.geoResult)
    },
  )

  const isAccessingUS = country === 'US'
  if (yoloCountry && isAccessingUS && onHide) {
    onHide()
    return null
  }
  const curFlag = `flag-${country.toLowerCase()}.svg`
  const destFlag = `flag-${yoloCountry ? 'US' : geoIPCountryCode.toLowerCase()}.svg`

  if (!data) {
    return null
  }

  return (
    <Modal hideCloseButton onHide={onHide} {...props}>
      <Title>{data.hl}</Title>
      <ButtonsWrapper>
        <Button
          color="dark"
          onClick={onHide}
          variant="outlined"
        >
          <div className="buttonContent">
            <Image
              alt={country}
              className="buttonImage"
              options={{
                height: 32,
                path: `frontend/country-flags/${curFlag}`,
                width: 32,
              }}
            />
            <span className="buttonText">{data.curr}</span>
          </div>
        </Button>
        <Button
          color="dark"
          onClick={() => {
            const hostname = window.location.hostname.split('trendhim')
            if (hostname.length === 1) {
              window.location.href = data.redirect
              return
            }
            const url = new URL(data.redirect)
            const { searchParams } = url
            searchParams.append('utm_source', `trendhim${hostname[hostname.length - 1]}`)
            searchParams.append('utm_medium', 'internal+referral')
            searchParams.append('utm_campaign', 'geolocation+overlay')
            window.location.href = url.toString()
          }}
          variant="outlined"
        >
          <div className="buttonContent">
            <Image
              alt={geoIPCountryCode}
              className="buttonImage"
              options={{
                height: 32,
                path: `frontend/country-flags/${destFlag}`,
                width: 32,
              }}
            />
            <span className="buttonText">{data.dist}</span>
          </div>
        </Button>
      </ButtonsWrapper>
    </Modal>
  )
}
